import * as authApi from '../api/authApi';
import {
  accessTokenExpiresAt,
  clearAuthSessionMeta,
  getAccessToken,
  persistTokenPair,
  readAuthSessionMeta,
} from './authSession';

/** Refresh this long before `exp` so in-flight requests never race expiry. */
export const REFRESH_SKEW_MS = 60 * 1000;

export const SESSION_REFRESHED_EVENT = 'evault:session-refreshed';
export const SESSION_EXPIRED_EVENT = 'evault:session-expired';

let inflight = null;

function emit(name, detail) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

export class SessionExpiredError extends Error {
  constructor(message = 'Your session has expired. Please sign in again.') {
    super(message);
    this.name = 'SessionExpiredError';
    this.status = 401;
  }
}

/**
 * Exchange the stored refresh token for a new access/refresh pair.
 *
 * The backend rotates refresh tokens (the old one is revoked as soon as it is
 * used), so concurrent callers MUST share one request — otherwise the second
 * caller would present an already-revoked token and log everyone out.
 */
export function refreshAccessToken() {
  if (inflight) return inflight;
  const meta = readAuthSessionMeta();
  if (!meta?.refreshToken) {
    return Promise.reject(new SessionExpiredError('No refresh token'));
  }
  inflight = authApi
    .refreshSession(meta.refreshToken)
    .then((tokenPair) => {
      const next = persistTokenPair(tokenPair, { authProvider: meta.authProvider });
      emit(SESSION_REFRESHED_EVENT, next);
      return next;
    })
    .catch((err) => {
      // The refresh token is gone (revoked, expired, Redis flushed). The
      // session cannot be recovered without signing in again.
      clearAuthSessionMeta();
      emit(SESSION_EXPIRED_EVENT, { reason: err?.message || 'refresh failed' });
      throw new SessionExpiredError();
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** True when there is a session that can be refreshed. */
export function canRefresh() {
  return Boolean(readAuthSessionMeta()?.refreshToken);
}

/** Milliseconds until the current access token should be refreshed (may be <= 0). */
export function msUntilRefreshDue(now = Date.now()) {
  const exp = accessTokenExpiresAt(getAccessToken());
  if (exp === null) return null;
  return exp - REFRESH_SKEW_MS - now;
}

/**
 * Make sure the in-memory access token is good for at least REFRESH_SKEW_MS.
 * Resolves to the current token (possibly refreshed), or null when there is
 * no JWT session at all (demo headers / signed out).
 */
export async function ensureFreshAccessToken() {
  const token = getAccessToken();
  if (!token) {
    if (canRefresh()) {
      await refreshAccessToken();
      return getAccessToken();
    }
    return null;
  }
  const due = msUntilRefreshDue();
  if (due !== null && due <= 0 && canRefresh()) {
    await refreshAccessToken();
    return getAccessToken();
  }
  return token;
}
